import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon } from '../../../icons';
import { formatDate } from '../../../lib/base-i18n';
import { useFormField } from '../FormField';
import { Calendar } from './Calendar';
import { fromDayKey, toDayKey } from './calendar-core';
import './DatePicker.css';

/**
 * DatePicker — `type="date"` **36회 / 22파일** 이 옮겨 올 자리 (디자인 정본 C1).
 *
 * ★★★ **값은 `YYYY-MM-DD` 문자열로 주고받는다.** `Date` 객체를 주고받으면 시각과 시간대가
 *    함께 따라다녀, 「9월 1일」이 시간대에 따라 8월 31일로 저장되는 일이 생긴다.
 *    근태·휴가는 **날짜만** 있으면 되는 값이다.
 *
 * ★★ **`new Date('2026-09-01')` 을 쓰지 않는다** — 그건 UTC 자정으로 읽혀 하루가 밀린다.
 *    한국에서는 안 밀리지만 그 사실에 기대면 시간대가 다른 곳에서 조용히 틀린다
 *    (`fromDayKey` 가 로컬 자정으로 만든다).
 *
 * ★ 보이는 글자는 `formatDate`(base-i18n) 가 만든다 — 로케일을 못 박지 않는다.
 */
/** 트리거와 달력 사이 · 창 가장자리 여백 — `portal` 좌표 계산에만 쓴다. */
const GAP = 4;
const EDGE = 8;

export interface DatePickerProps {
  /** `YYYY-MM-DD` 또는 빈 값. */
  value: string | null;
  onChange: (next: string | null) => void;
  min?: string | null;
  max?: string | null;
  /** 고를 수 없는 날 — 휴무일 등. `YYYY-MM-DD`. */
  disabledDays?: ReadonlySet<string>;
  holidays?: ReadonlySet<string>;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** 지우기 단추를 보인다. */
  clearable?: boolean;
  weekStart?: 0 | 1;
  ariaLabel?: string;
  className?: string;
  /**
   * 달력을 **body 로 띄운다** (2026-09-06 추가, 기본은 꺼짐).
   *
   * ★★★ **왜 필요했나** — `overflow` 가 걸린 상자(표의 가로 스크롤 · 모달 본문) 안에서는
   *    달력이 **잘린다.** 한 축이 `auto` 면 나머지 축도 `auto` 가 되어(스펙) 세로로도 잘리는데,
   *    화면에는 «스크롤만 생기고 날짜를 못 고르는» 모습으로 나타나 원인을 짚기 어렵다.
   *    `Combobox` 가 같은 이유로 먼저 이 문을 냈다 — **표 안 · 모달 안이면 켠다.**
   * ★ 켜면 팝오버가 root 밖으로 나가므로 **바깥 클릭 판정도 달라진다**(둘 다 본다).
   */
  portal?: boolean;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  disabledDays,
  holidays,
  placeholder = '날짜를 고르세요',
  disabled,
  invalid,
  clearable = false,
  weekStart = 0,
  ariaLabel,
  className,
  portal = false,
}: DatePickerProps) {
  const field = useFormField();
  const uid = useId();
  const popId = `${uid}-pop`;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** `portal` 일 때만 쓰는 좌표. 꺼져 있으면 늘 null 이라 **종전 경로 그대로**다. */
  const popRef = useRef<HTMLDivElement>(null);
  const [fixedStyle, setFixedStyle] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const selectedDate = value ? fromDayKey(value) : null;
  const [month, setMonth] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (selectedDate) setMonth(selectedDate);
    // value 가 바뀌면 그 달을 보여 준다 — 밖에서 값을 바꿨을 때 엉뚱한 달이 열리지 않게.
  }, [value]);

  /** 밖을 누르면 닫는다. 안에서 달을 넘기는 클릭은 닫으면 안 되므로 root 로 판정한다. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      // ★ portal 이면 달력이 root **밖**에 있다 — 둘 다 봐야 달을 넘기다 닫히지 않는다.
      if (rootRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc, true);
    };
  }, [open]);

  /**
   * `portal` 일 때의 좌표 — **트리거 자리를 재서** 정한다.
   * ★ 아래가 모자라면 위로 연다. 옆이 모자라면 창 안쪽으로 당긴다.
   *   달력은 크기가 정해져 있어 높이를 못박지 않는다(목록과 다른 점).
   */
  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const w = popRef.current?.offsetWidth ?? 280;
    const h = popRef.current?.offsetHeight ?? 320;
    const below = window.innerHeight - r.bottom - GAP - EDGE;
    const up = below < h && r.top - GAP - EDGE > below;
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - w - EDGE));
    setFixedStyle(up
      ? { left, bottom: window.innerHeight - r.top + GAP }
      : { left, top: r.bottom + GAP });
  }, []);

  /* 스크롤·창 크기를 «따라간다» — 닫아 버리면 표를 조금만 굴려도 달력이 사라진다.
     scroll 은 capture 로 듣는다(표의 가로 스크롤처럼 «안쪽» 상자가 구르는 것이 이 기능의 이유다). */
  useLayoutEffect(() => {
    if (!portal || !open) return;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [portal, open, place]);

  const isDisabled = disabled ?? field?.disabled ?? false;
  const isInvalid = invalid ?? field?.invalid ?? false;

  const pop = (
    <div
      ref={popRef}
      className={`wc-datepicker__pop${portal ? ' wc-datepicker__pop--fixed' : ''}`}
      style={portal ? (fixedStyle ?? undefined) : undefined}
      id={popId}
      role="dialog"
      aria-label="날짜 고르기"
    >
      <Calendar
        month={month}
        onMonthChange={setMonth}
        selected={selectedDate ? [selectedDate] : []}
        onSelect={(d) => { onChange(toDayKey(d)); setOpen(false); }}
        min={min ? fromDayKey(min) : null}
        max={max ? fromDayKey(max) : null}
        disabledDays={disabledDays}
        holidays={holidays}
        weekStart={weekStart}
      />
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={['wc-datepicker', isInvalid ? 'wc-datepicker--invalid' : '', className ?? ''].filter(Boolean).join(' ')}
    >
      <button
        type="button"
        ref={triggerRef}
        id={field?.controlId}
        className="wc-datepicker__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-describedby={field?.describedBy}
        aria-invalid={isInvalid || undefined}
        aria-required={field?.required || undefined}
        aria-label={field?.controlId ? undefined : (ariaLabel ?? placeholder)}
        disabled={isDisabled}
        onClick={() => setOpen((v) => !v)}
      >
        <CalendarIcon className="wc-datepicker__icon" aria-hidden />
        <span className={value ? 'wc-datepicker__value' : 'wc-datepicker__placeholder'}>
          {value ? formatDate(selectedDate ?? value) : placeholder}
        </span>
      </button>

      {clearable && value && !isDisabled && (
        <button type="button" className="wc-datepicker__clear" aria-label="날짜 지우기"
          onClick={() => onChange(null)}>
          ×
        </button>
      )}

      {open && (portal ? createPortal(pop, document.body) : pop)}
    </div>
  );
}
